import { FC, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  ChevronDown,
  ChevronUp,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import LoadingSpinner from "src/components/reusable/LoadingSpinner";
import {
  DefinitionTooltip,
  compareDashboardValues,
  DashboardNarrativePanel,
  DashboardSectionHeader,
  DashboardExportButton,
  type SortDirection,
} from "src/components/common";
import { useDashboardNarrative } from "src/hooks/UseDashboardNarrative";
import { useDashboardPdfRowOverride } from "src/hooks/UseDashboardPdfTableExpansion";
import {
  usePostCaptions,
  useFacebookContentReport,
} from "../../../hooks/UseOrganicMediaData";
import {
  formatOrganicPostTypeLabel,
  getOrganicEngagementDefinitionInfo,
} from "../OrganicMediaDefinitions";
import OrganicDateRangePicker from "src/components/common/OrganicDateRangePicker/OrganicDateRangePicker";
import OrganicSelectMenu from "../OrganicSelectMenu";
import { normalizeApiLang } from "src/utils/apiLang";
import { useLanguage } from "src/hooks/UseLanguage";
import { formatNumber } from "src/utils/numberFormat";
import { resolveDashboardChangePercent } from "src/utils/dashboardChangeFallback";
import { downloadCsv } from "src/dashboards/paidMedia/utils/PaidMediaCsvExport";

import FacebookContentKpiCards from "./FacebookContentKpiCards";
import FacebookContentTypeBreakdown from "./FacebookContentTypeBreakdown";
import FacebookContentPerformanceByType from "./FacebookContentPerformanceByType";
import FacebookContentTrends from "./FacebookContentTrends";
import FacebookContentPostTable from "./FacebookContentPostTable";
import {
  formatTrendMetricLabel,
  formatFacebookEngagementLabel,
  getCaptionPreview,
  getMonthBucket,
  getPercentAxisConfig,
  getPostTypeMetricFormattedValue,
  getTrendState,
  parseDateValueToMs,
  truncateCaption,
  withBidiIsolate,
  CONTENT_BREAKDOWN_COLORS,
  CONTENT_TYPE_BAR_PAGE_SIZE,
  FACEBOOK_REACTION_KEYS,
  ORGANIC_CHART_COLORS,
  PERCENT_METRICS,
  POSTS_PER_PAGE,
  type DateRange,
  type MetricKey,
  type TrendGranularity,
  type EngagementCardRow,
  type EngagementDetailRow,
  type PercentAxisConfig,
  type PostTableSortKey,
  type ReactionMenuPosition,
} from "./FacebookContentHelpers";
import "../../../styles/FacebookContentReport/FacebookContentReport.scss";
import "../../../styles/FacebookContentReport/FacebookContentEngagement.scss";


export interface OrganicDateFilters {
  appliedStartDate: string;
  appliedEndDate: string;
  draftStartDate: string;
  draftEndDate: string;
  // eslint-disable-next-line no-unused-vars
  onApplyDates(range: DateRange): void;
}

interface FacebookContentReportProps {
  dateFilters: OrganicDateFilters;
}

const FacebookContentReport: FC<FacebookContentReportProps> = ({
  dateFilters,
}) => {
  const { t, i18n } = useTranslation();
  const { isRTL } = useLanguage();
  const apiLang = normalizeApiLang(i18n.language);
  const {
    appliedStartDate,
    appliedEndDate,
    draftStartDate,
    draftEndDate,
    onApplyDates,
  } = dateFilters;
  const [postCaption, setPostCaption] = useState("(All)");
  const [selectedMetric, setSelectedMetric] = useState<MetricKey>(
    "engagementRate"
  );
  const [trendGranularity, setTrendGranularity] = useState<TrendGranularity>("daily");
  const [contentTypeBarPage, setContentTypeBarPage] = useState(1);
  const [currentPostPage, setCurrentPostPage] = useState(1);
  const [postTableSearch, setPostTableSearch] = useState("");
  const [postTableSort, setPostTableSort] = useState<{
    key: PostTableSortKey;
    direction: SortDirection;
  } | null>(null);
  const [isReactionMenuOpen, setIsReactionMenuOpen] = useState(false);
  const [reactionMenuPosition, setReactionMenuPosition] =
    useState<ReactionMenuPosition | null>(null);
  const reactionCardRef = useRef<HTMLDivElement | null>(null);
  const reactionDropdownRef = useRef<HTMLDivElement | null>(null);
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const [isPdfCapturing, setIsPdfCapturing] = useState(false);

  const { postCaptions } = usePostCaptions(appliedStartDate, appliedEndDate, apiLang);
  const {
    kpis,
    totalEngagements,
    reactionsPercentageChange,
    reactionsPercentageChangeFormattedValue,
    postsByType,
    postTypeMetrics,
    postDetails,
    loading,
    error,
  } = useFacebookContentReport(
    appliedStartDate,
    appliedEndDate,
    postCaption,
    apiLang,
    trendGranularity,
  );

  const fbNarrativePayload = useMemo<Record<string, unknown>>(
    () => ({
      kpis,
      total_engagements: totalEngagements,
      posts_by_type: postsByType,
      post_type_metrics: postTypeMetrics,
      post_details: postDetails.slice(0, 20),
      trend_kpi: { daily: kpis.flatMap(k => k.trend), monthly: [] },
    }),
    [kpis, totalEngagements, postsByType, postTypeMetrics, postDetails],
  );

  const { narrative: fbNarrative, loading: fbNarrativeLoading, aiSummary } = useDashboardNarrative({
    dashboardKey: "organic_media",
    tabKey: "facebook_content_report",
    startDate: appliedStartDate,
    endDate: appliedEndDate,
    data: fbNarrativePayload,
    promptVersion: "v6",
    enabled: !loading && !error && Boolean(kpis.length),
  });

  const contentTypeColorMap = useMemo(
    () =>
      new Map(
        postsByType.map((item, index) => [
          String(item.name).toLowerCase(),
          CONTENT_BREAKDOWN_COLORS[index % CONTENT_BREAKDOWN_COLORS.length],
        ])
      ),
    [postsByType]
  );

  const chartPostsByType = useMemo(
    () =>
      postsByType.map((item, index) => ({
        ...item,
        color: CONTENT_BREAKDOWN_COLORS[index % CONTENT_BREAKDOWN_COLORS.length],
      })),
    [postsByType]
  );

  const rightChartData = useMemo(
    () =>
      postTypeMetrics
        .map(row => ({
          type: row.type,
          value: row[selectedMetric],
          valueFormattedValue: getPostTypeMetricFormattedValue(row, selectedMetric),
          color:
            contentTypeColorMap.get(String(row.type).toLowerCase()) ??
            ORGANIC_CHART_COLORS[0],
        }))
        .sort((a, b) => (b.value ?? 0) - (a.value ?? 0)),
    [contentTypeColorMap, postTypeMetrics, selectedMetric]
  );

  const contentTypeBarTotalPages = Math.max(
    1,
    Math.ceil(rightChartData.length / CONTENT_TYPE_BAR_PAGE_SIZE)
  );
  const pagedRightChartData = useMemo(() => {
    const startIndex = (contentTypeBarPage - 1) * CONTENT_TYPE_BAR_PAGE_SIZE;
    return rightChartData.slice(startIndex, startIndex + CONTENT_TYPE_BAR_PAGE_SIZE);
  }, [contentTypeBarPage, rightChartData]);

  const rightChartAxisConfig = useMemo<PercentAxisConfig>(() => {
    if (!PERCENT_METRICS.includes(selectedMetric)) {
      return {};
    }

    return getPercentAxisConfig(rightChartData.map(row => Number(row.value ?? 0)));
  }, [rightChartData, selectedMetric]);

  const pieLegendItems = useMemo(() => {
    const total = chartPostsByType.reduce((sum, item) => sum + Number(item.value ?? 0), 0);
    if (!total) {
      return [];
    }

    return [...chartPostsByType].sort((a, b) => Number(b.value ?? 0) - Number(a.value ?? 0));
  }, [chartPostsByType]);

  const pieLegendTotal = useMemo(
    () => pieLegendItems.reduce((sum, item) => sum + Number(item.value ?? 0), 0),
    [pieLegendItems]
  );

  const trendComparisonCards = useMemo(() => {

    const score = (metricKey: string) => {
      if (metricKey.includes("impression")) {return 3;}
      if (metricKey.includes("reach")) {return 2;}
      if (metricKey.includes("engagement")) {return 1;}
      return 0;
    };

    const sorted = [...kpis].sort((a, b) => score(b.metricKey) - score(a.metricKey));
    return sorted.slice(0, 2);
  }, [kpis]);

  const trendComparisonData = useMemo(() => {
    const byDate = new Map<string, Record<string, string | number>>();

    trendComparisonCards.forEach(card => {
      card.trend.forEach(point => {
        const dateKey = trendGranularity === "monthly"
          ? getMonthBucket(point.date)
          : point.date;
        const row = byDate.get(dateKey) ?? { date: dateKey };
        row[card.title] = Number(row[card.title] ?? 0) + Number(point.value ?? 0);
        byDate.set(dateKey, row);
      });
    });

    return Array.from(byDate.values()).sort((a, b) => {
      const aMs = parseDateValueToMs(String(a.date ?? ""));
      const bMs = parseDateValueToMs(String(b.date ?? ""));
      if (Number.isNaN(aMs) || Number.isNaN(bMs)) {
        return String(a.date).localeCompare(String(b.date));
      }
      return aMs - bMs;
    });
  }, [trendComparisonCards, trendGranularity]);

  const trendLegendItems = useMemo(
    () =>
      trendComparisonCards.map((card, index) => ({
        label: formatTrendMetricLabel(card.title, t),
        color: ORGANIC_CHART_COLORS[index % ORGANIC_CHART_COLORS.length],
      })),
    [trendComparisonCards, t]
  );

  const engagementCards = useMemo(
    (): EngagementCardRow[] => {
      const reactionRows = totalEngagements.filter(row =>
        FACEBOOK_REACTION_KEYS.includes(row.metricKey)
      );
      const primaryRows: EngagementCardRow[] = totalEngagements.filter(
        row => !FACEBOOK_REACTION_KEYS.includes(row.metricKey)
      );
      const totalReactionValue = reactionRows.reduce(
        (sum, row) => sum + Number(row.value ?? 0),
        0
      );
      const cards = [...primaryRows];

      // Individual reaction types (like/love/haha/...) rarely carry their own
      // percentageChange — only average the ones that actually have one, so a
      // row full of "no data" doesn't average out to a fabricated 0.
      const reactionChangesWithData = reactionRows
        .map(row => row.percentageChange)
        .filter((value): value is number => typeof value === "number" && Number.isFinite(value));
      const averagedReactionChange =
        reactionChangesWithData.length > 0
          ? reactionChangesWithData.reduce((sum, value) => sum + value, 0) /
            reactionChangesWithData.length
          : undefined;

      cards.push({
        metricKey: "reactions",
        // Client-summed across the individual reaction types below — the
        // backend never sends a single "total reactions" count, so there is
        // no FormattedValue sibling for this aggregate.
        value: totalReactionValue,
        percentageChange: reactionsPercentageChange ?? averagedReactionChange,
        // Only valid when the backend's own aggregate change is used — the
        // averaged fallback is client-derived and has no matching string.
        percentageChangeFormattedValue:
          reactionsPercentageChange !== undefined
            ? reactionsPercentageChangeFormattedValue
            : undefined,
        comparisonLabel: t("organicMedia.facebook.vsLastPeriod"),
        trend: [],
        details: reactionRows,
      });

      return cards;
    },
    [totalEngagements, reactionsPercentageChange, reactionsPercentageChangeFormattedValue, t]
  );

  const activeReactionDetails =
    engagementCards.find(row => row.metricKey === "reactions")?.details ?? [];

  const handleRangeChange = (range: DateRange) => {
    setPostCaption("(All)");
    onApplyDates(range);
  };

  const filteredPostDetails = useMemo(() => {
    const query = postTableSearch.trim().toLowerCase();
    const startMs = parseDateValueToMs(appliedStartDate);
    const endMs = parseDateValueToMs(appliedEndDate, true);

    return postDetails.filter(row => {
      const rowDateMs = parseDateValueToMs(String(row.createdAt ?? ""));
      const isInSelectedRange =
        Number.isNaN(rowDateMs) ||
        Number.isNaN(startMs) ||
        Number.isNaN(endMs) ||
        (rowDateMs >= startMs && rowDateMs <= endMs);
      if (!isInSelectedRange) {
        return false;
      }
      if (!query) {
        return true;
      }
      const caption = String(row.caption ?? "").toLowerCase();
      return caption.includes(query);
    });
  }, [appliedEndDate, appliedStartDate, postDetails, postTableSearch]);

  const sortedPostDetails = useMemo(() => {
    if (!postTableSort) {
      return filteredPostDetails;
    }

    return [...filteredPostDetails].sort((left, right) => {
      switch (postTableSort.key) {
        case "caption":
          return compareDashboardValues(left.caption, right.caption, postTableSort.direction);
        case "postType":
          return compareDashboardValues(left.postType, right.postType, postTableSort.direction);
        case "impressions":
          return compareDashboardValues(left.impressions, right.impressions, postTableSort.direction);
        case "clicks":
          return compareDashboardValues(left.clicks, right.clicks, postTableSort.direction);
        case "loves":
          return compareDashboardValues(left.loves, right.loves, postTableSort.direction);
        case "comments":
          return compareDashboardValues(left.comments, right.comments, postTableSort.direction);
        case "engagementRate":
          return compareDashboardValues(left.engagementRate, right.engagementRate, postTableSort.direction);
        default:
          return 0;
      }
    });
  }, [filteredPostDetails, postTableSort]);
  const totalPostPages = Math.max(1, Math.ceil(sortedPostDetails.length / POSTS_PER_PAGE));
  const paginatedPostDetails = useMemo(() => {
    const startIndex = (currentPostPage - 1) * POSTS_PER_PAGE;
    return sortedPostDetails.slice(startIndex, startIndex + POSTS_PER_PAGE);
  }, [currentPostPage, sortedPostDetails]);

  const {
    rows: exportPostDetails,
    onBeforeCapture: onBeforePostTableCapture,
    onAfterCapture: onAfterPostTableCapture,
  } = useDashboardPdfRowOverride(paginatedPostDetails, async () => sortedPostDetails);

  useEffect(() => {
    setCurrentPostPage(1);
  }, [appliedStartDate, appliedEndDate, postCaption, postDetails.length, postTableSort, postTableSearch]);

  useEffect(() => {
    if (currentPostPage > totalPostPages) {
      setCurrentPostPage(totalPostPages);
    }
  }, [currentPostPage, totalPostPages]);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        reactionCardRef.current &&
        !reactionCardRef.current.contains(target) &&
        !reactionDropdownRef.current?.contains(target)
      ) {
        setIsReactionMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, []);

  useEffect(() => {
    if (!isReactionMenuOpen) {
      setReactionMenuPosition(null);
      return;
    }

    const updatePosition = () => {
      const rect = reactionCardRef.current?.getBoundingClientRect();
      if (!rect) {
        return;
      }

      const width = Math.min(Math.max(244, rect.width - 28), 320);
      setReactionMenuPosition({
        top: rect.top + 34,
        left: Math.max(8, Math.min(window.innerWidth - width - 8, rect.right - width - 14)),
        width,
      });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isReactionMenuOpen]);

  useEffect(() => {
    setContentTypeBarPage(1);
  }, [selectedMetric]);

  useEffect(() => {
    if (contentTypeBarPage > contentTypeBarTotalPages) {
      setContentTypeBarPage(contentTypeBarTotalPages);
    }
  }, [contentTypeBarPage, contentTypeBarTotalPages]);

  const handleExportPosts = () => {
    downloadCsv(
      `facebook_content_posts_${appliedStartDate}_to_${appliedEndDate}.csv`,
      [
        t("organicMedia.facebook.table.caption"),
        t("organicMedia.facebook.table.type"),
        t("organicMedia.facebook.table.impressions"),
        t("organicMedia.facebook.table.clicks"),
        t("organicMedia.facebook.table.likes"),
        t("organicMedia.facebook.table.comments"),
        t("organicMedia.facebook.table.engRate"),
        t("organicMedia.facebook.table.link"),
      ],
      sortedPostDetails.map(row => [
        row.caption,
        formatOrganicPostTypeLabel(row.postType, t),
        row.impressions,
        row.clicks,
        row.loves,
        row.comments,
        row.engagementRateFormattedValue ??
          `${formatNumber(row.engagementRate, { minimumFractionDigits: 1, maximumFractionDigits: 1 }, i18n.language)}%`,
        row.postUrl,
      ])
    );
  };

  const reactionDropdown =
    isReactionMenuOpen &&
    activeReactionDetails.length > 0 &&
    reactionMenuPosition &&
    typeof document !== "undefined"
      ? createPortal(
        <div
          ref={reactionDropdownRef}
          className="organic-engagement-card__dropdown organic-engagement-card__dropdown--portal"
          role="menu"
          style={{
            top: reactionMenuPosition.top,
            left: reactionMenuPosition.left,
            width: reactionMenuPosition.width,
          }}
        >
          {activeReactionDetails.map((detail: EngagementDetailRow) => {
            const detailTrendPct = resolveDashboardChangePercent(
              detail.percentageChange,
              `facebook-content-engagement-detail-${detail.metricKey}`,
              { treatZeroAsMissing: true }
            );
            const hasDetailTrend =
              typeof detail.percentageChange === "number" &&
              Number.isFinite(detail.percentageChange);
            return (
              <div
                key={detail.metricKey}
                className="organic-engagement-card__dropdown-row"
              >
                <div className="organic-engagement-card__dropdown-metric">
                  <span className="organic-engagement-card__dropdown-label">
                    {formatFacebookEngagementLabel(detail.metricKey, t)}
                  </span>
                  <strong className="organic-engagement-card__dropdown-value">
                    {detail.valueFormattedValue ?? formatNumber(detail.value, {}, i18n.language)}
                  </strong>
                </div>
                {hasDetailTrend ? (
                  <span
                    className={`organic-engagement-card__dropdown-trend organic-engagement-card__dropdown-trend--${getTrendState(detailTrendPct)}`}
                  >
                    {detailTrendPct === 0 ? null : detailTrendPct > 0 ? (
                      <TrendingUp size={11} aria-hidden />
                    ) : (
                      <TrendingDown size={11} aria-hidden />
                    )}
                    {detail.percentageChangeFormattedValue ??
                      withBidiIsolate(
                        `${formatNumber(detailTrendPct, { minimumFractionDigits: 1, maximumFractionDigits: 1 }, i18n.language)}%`,
                        i18n.language
                      )}
                  </span>
                ) : null}
              </div>
            );
          })}
        </div>,
        document.body
      )
      : null;

  return (
    <div
      className={`organic-content-report${isPdfCapturing ? " organic-content-report--pdf-capture" : ""}`}
      ref={pdfContainerRef}
    >
      <DashboardSectionHeader
        className="organic-content-report__section-header"
        title={t("organicMedia.tabs.facebookContent")}
        subtitle={t("organicMedia.contentReport.facebookSubtitle")}
        actionsClassName="dashboard-section-header__actions--filters"
        actions={
          <>
            <div className="dashboard-section-header__control dashboard-section-header__control--wide">
              <OrganicDateRangePicker
                startDate={draftStartDate}
                endDate={draftEndDate}
                onRangeChange={handleRangeChange}
              />
            </div>
            <div className="dashboard-section-header__control">
              <OrganicSelectMenu
                className="organic-content-report__caption-select"
                aria-label={t("organicMedia.common.postCaptionFilter")}
                value={postCaption}
                onChange={setPostCaption}
                options={postCaptions.map(option => ({
                  value: option,
                  label:
                    option === "(All)"
                      ? t("organicMedia.common.allCaptionsOption")
                      : truncateCaption(option),
                  title:
                    option === "(All)"
                      ? t("organicMedia.common.allCaptionsOption")
                      : getCaptionPreview(option),
                }))}
              />
            </div>
            <DashboardExportButton
              dashboardName="Facebook Content"
              dateRange={{ startDate: appliedStartDate, endDate: appliedEndDate }}
              containerRef={pdfContainerRef}
              disabled={loading || Boolean(error)}
              onBeforeCapture={async () => {
                setIsPdfCapturing(true);
                await onBeforePostTableCapture();
              }}
              onAfterCapture={() => {
                onAfterPostTableCapture();
                setIsPdfCapturing(false);
              }}
            />
          </>
        }
      />

      {error && (
        <div className="organic-tabs-dashboard__error" role="alert">
          {error}
        </div>
      )}

      <div data-pdf-block="narrative">
        <DashboardNarrativePanel
          narrative={fbNarrative}
          loading={loading || fbNarrativeLoading}
          aiSummary={aiSummary}
        />
      </div>

      {loading ? (
        <div className="organic-content-report__loading">
          <LoadingSpinner compact />
        </div>
      ) : (
        <>
          <FacebookContentKpiCards kpis={kpis} />

          <div className="organic-content-report__engagement-block" data-pdf-block="engagement">
            <div className="organic-content-report__engagement-grid">
              {engagementCards.map((row, index) => {
                const reactionDetails = row.details;
                const engagementLabel = formatFacebookEngagementLabel(row.metricKey, t);
                const engagementTrendPct = resolveDashboardChangePercent(
                  row.percentageChange,
                  `facebook-content-engagement-${row.metricKey}`,
                  { treatZeroAsMissing: true }
                );
                // A null change means the backend has no comparison for this
                // metric, so hide the badge entirely; a real `0` is a genuine
                // "no change" and is shown as "0.0%".
                const hasEngagementTrend =
                  typeof row.percentageChange === "number" &&
                  Number.isFinite(row.percentageChange);
                return (
                  <div
                    key={row.metricKey}
                    className={`organic-engagement-card ${
                      reactionDetails?.length ? "organic-engagement-card--dropdown" : ""
                    } ${
                      reactionDetails?.length && isReactionMenuOpen
                        ? "organic-engagement-card--dropdown-open"
                        : ""
                    }`}
                    ref={reactionDetails?.length ? reactionCardRef : undefined}
                  >
                    <div className="organic-engagement-card__label-row">
                      <div className="organic-engagement-card__label">
                        <span>{engagementLabel}</span>
                        <DefinitionTooltip
                          label={engagementLabel}
                          info={getOrganicEngagementDefinitionInfo(row.metricKey, engagementLabel, t, "facebook")}
                          align={index >= 3 ? "end" : "start"}
                          formulaTitle={t("organicMedia.common.formula")}
                        />
                      </div>
                      {reactionDetails?.length ? (
                        <button
                          type="button"
                          className="organic-engagement-card__toggle"
                          aria-label={t("organicMedia.common.breakdown", {
                            label: engagementLabel,
                          })}
                          aria-expanded={isReactionMenuOpen}
                          onClick={() => setIsReactionMenuOpen(open => !open)}
                        >
                          {isReactionMenuOpen ? (
                            <ChevronUp size={14} aria-hidden />
                          ) : (
                            <ChevronDown size={14} aria-hidden />
                          )}
                        </button>
                      ) : null}
                    </div>
                    <div className="organic-engagement-card__value">
                      {row.valueFormattedValue ?? formatNumber(row.value, {}, i18n.language)}
                    </div>
                    {hasEngagementTrend ? (
                      <div className="organic-engagement-card__period">
                        <span
                          className={`organic-engagement-card__change organic-engagement-card__change--${getTrendState(engagementTrendPct)}`}
                        >
                          {engagementTrendPct === 0 ? null : engagementTrendPct > 0 ? (
                            <TrendingUp size={13} aria-hidden />
                          ) : (
                            <TrendingDown size={13} aria-hidden />
                          )}
                          {row.percentageChangeFormattedValue ??
                            withBidiIsolate(
                              `${formatNumber(engagementTrendPct, { minimumFractionDigits: 1, maximumFractionDigits: 1 }, i18n.language)}%`,
                              i18n.language
                            )}
                        </span>
                        <span>{row.comparisonLabel}</span>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
            {reactionDropdown}
          </div>

          <div className="organic-content-report__chart-grid" data-pdf-block="charts">
            <FacebookContentTypeBreakdown
              chartPostsByType={chartPostsByType}
              pieLegendItems={pieLegendItems}
              pieLegendTotal={pieLegendTotal}
            />

            <FacebookContentPerformanceByType
              selectedMetric={selectedMetric}
              setSelectedMetric={setSelectedMetric}
              pagedRightChartData={pagedRightChartData}
              rightChartData={rightChartData}
              rightChartAxisConfig={rightChartAxisConfig}
              contentTypeBarPage={contentTypeBarPage}
              setContentTypeBarPage={setContentTypeBarPage}
              contentTypeBarTotalPages={contentTypeBarTotalPages}
              isRTL={isRTL}
            />

            <FacebookContentTrends
              trendGranularity={trendGranularity}
              setTrendGranularity={setTrendGranularity}
              trendComparisonData={trendComparisonData}
              trendComparisonCards={trendComparisonCards}
              trendLegendItems={trendLegendItems}
            />
          </div>

          <FacebookContentPostTable
            props={{
              t,
              i18n,
              sortedPostDetails,
              postTableSearch,
              setPostTableSearch,
              handleExportPosts,
              setPostTableSort,
              getCaptionPreview,
              truncateCaption,
              currentPostPage,
              setCurrentPostPage,
              totalPostPages,
              exportPostDetails,
            }}
          />
        </>
      )}
    </div>
  );
};

export default FacebookContentReport;
